import { Button, Flex, Stack } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import AlertModal from '../../../components/modals/AlertModal';
import { DataTable } from '../../../components/Table/DataTable';
import useDeleteWhiteLabelOrganizationUserMutation from '../../../hooks/mutations/useDeleteWhiteLabelOrganizationUserMutation';
import useWhiteLabelOrganizationUsersQuery from '../../../hooks/useWhiteLabelOrganizationUsersQuery';
import { ContentCard } from '../../../layout/ContentCard';

const WhiteLabelOrganizationUsersCard = (props: { whiteLabelOrganizationId: number }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { whiteLabelOrganizationId } = props;
  const deleteWhiteLabelOrganizationUserMutation = useDeleteWhiteLabelOrganizationUserMutation(['Whitelabel Organization Users']);

  const whiteLabelOrganizationUsersQuery = useWhiteLabelOrganizationUsersQuery();
  const whiteLabelUsers = whiteLabelOrganizationUsersQuery.data?.data || [];
  const columns = useMemo(
    () => [
      {
        Header: t('forms:firstname.label'),
        accessor: 'firstname',
      },
      {
        Header: t('forms:lastname.label'),
        accessor: 'lastname',
      },
      {
        Header: t('forms:email.label'),
        accessor: 'email',
      },
      {
        Header: t('forms:status.label'),
        accessor: 'accepted',
        Cell: ({ value }: any) => (
          <>
            {value
              ? t('pages:whitelabel_organizations.organization.invitation_status.accepted')
              : t('pages:whitelabel_organizations.organization.invitation_status.not_accepted')}
          </>
        ),
      },
      {
        id: 'button',
        accessor: 'id',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const userId = row.original.id;
          return (
            <Flex alignContent={'flex-end'}>
              <Button
                ml={'auto'}
                color={'white'}
                backgroundColor={'red.600'}
                borderColor={'red.600'}
                //@ts-ignore
                sx={{
                  _hover: { color: 'red.600', backgroundColor: 'white' },
                }}
                variant="outline"
                onClick={() => {
                  NiceModal.show(AlertModal, {
                    onSubmit: () =>
                      deleteWhiteLabelOrganizationUserMutation.mutateAsync(userId).then(() => {
                        navigate(`/app/op/whitelabel-organizations/${whiteLabelOrganizationId}`);
                      }),
                    children: t('pages:whitelabel_organizations.organization.users.delete.message'),
                    redSubmitBttn: true,
                    content: {
                      header: t('pages:whitelabel_organizations.organization.users.delete.title'),
                      footer: {
                        buttons: {
                          cancelCaption: t('common:cancel'),
                          actionCaption: t('common:delete'),
                        },
                      },
                    },
                  });
                }}
              >
                {t('common:delete')}
              </Button>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [whiteLabelUsers],
  );
  if (whiteLabelOrganizationUsersQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard minH="fit-content" header={t('pages:whitelabel_organizations.organization.users.admin_users')} data-test-id="users-overview-card">
      <Button
        mb={10}
        onClick={() => {
          navigate(`/app/op/whitelabel-organizations/create`);
        }}
        variant="primary"
        disabled
      >
        {t('pages:whitelabel_organizations.organization.users.invite_admin_user')}
      </Button>
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            data-test-id="users-table"
            data={whiteLabelUsers}
            hasFilters={false}
            columns={columns}
            loading={whiteLabelOrganizationUsersQuery.isLoading}
            isMoreThenOnePage={false}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WhiteLabelOrganizationUsersCard;
